/**
 * Builds a choices array from selections for use with buildPersonalisationChoices.
 * This is extracted from PersonalisationContext.jsx to allow reuse in tests.
 *
 * @param {Object} params
 * @param {Object} params.selections - The selections object containing slots
 * @returns {Array} Array of choice payloads
 */
export function buildChoicesFromSelections({ selections }) {
  const choices = [];

  selections.slots
    .filter(({ selected }) => selected !== null && selected !== undefined)
    .forEach(
      ({
        componentGroupId,
        selected,
        id,
        slotType,
        lineItemPropertyKey,
        translatedLineItemPropertyKey,
        stepId,
        slotMode,
        extraImages,
        visualisationGroup,
        supportingImage,
        supportingImageLineItemPropertyKey
      }) => {
        // For ALPHANUMERIC slots, provide a default lineItemPropertyKey if missing.
        const effectiveLineItemPropertyKey =
          slotType === 'ALPHANUMERIC' && !lineItemPropertyKey ? 'text' : lineItemPropertyKey;

        if (Array.isArray(selected)) {
          // For upsells with multiple selections, create a choice for each selected variant
          selected.forEach((variantId, index) => {
            // Skip null or undefined variantIds
            if (!variantId) return;

            choices.push({
              componentGroupId,
              variantId,
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey,
              lineItemPropertyKeyValue: variantId,
              slotType,
              slotMode,
              index
            });
          });
        } else {
          // Skip null or undefined selected values
          if (!selected) return;

          // Handle ALPHANUMERIC slots differently - extract text and font
          if (slotType === 'ALPHANUMERIC' && typeof selected === 'object' && selected.text) {
            choices.push({
              componentGroupId: null, // ALPHANUMERIC slots don't have component groups
              variantId: selected.text, // Use the text as the variantId
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey,
              lineItemPropertyKeyValue: selected.text, // The actual text content
              slotType,
              slotMode,
              font: selected.font, // Add the font selection
              visualisationGroup: visualisationGroup?.groupId || null, // Pass visualisationGroup.groupId for matching visData
              supportingImageLineItemPropertyKey:
                visualisationGroup?.supportingImageLineItemPropertyKey || supportingImageLineItemPropertyKey || null // Pass supporting image line item property key for warehouse use (from visualisationGroup or slot)
            });
          } else if (selected instanceof File) {
            // Handle IMAGE_UPLOAD slots - pass the File object as lineItemPropertyKeyValue
            choices.push({
              componentGroupId: null, // IMAGE_UPLOAD slots don't have component groups
              variantId: null, // No variant for image uploads
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey,
              lineItemPropertyKeyValue: selected, // Pass the File object (edited image)
              slotType,
              slotMode,
              extraImages: extraImages || [], // Include extraImages for IMAGE_UPLOAD slots
              visualisationGroup: visualisationGroup?.groupId || null,
              supportingImage: supportingImage || null,
              supportingImageLineItemPropertyKey:
                visualisationGroup?.supportingImageLineItemPropertyKey || supportingImageLineItemPropertyKey || null
            });
          } else {
            // For single selections, create one choice
            choices.push({
              componentGroupId,
              variantId: selected,
              slotId: id,
              stepId,
              lineItemPropertyKey: effectiveLineItemPropertyKey,
              translatedLineItemPropertyKey,
              lineItemPropertyKeyValue: selected,
              slotType,
              slotMode
            });
          }
        }
      }
    );

  return choices;
}
